import { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import API from '../api/axios';
import ModeratorCategorizedSetsView from '../components/ModeratorCategorizedSetsView';
import './UniversityPoolModerator.css';

const UniversityPoolModerator = () => {
  const { user } = useContext(AuthContext);
  const { showToast } = useToast();
  
  const [activeTab, setActiveTab] = useState('pools');
  const [poolsReady, setPoolsReady] = useState([]);
  const [selectedPool, setSelectedPool] = useState(null);
  const [selectedVariations, setSelectedVariations] = useState(null);
  const [generatedSets, setGeneratedSets] = useState([]);
  const [setsByCategory] = useState({ general: [], special: [], reexam: [] });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sendingToCOE, setSendingToCOE] = useState(false);
  const [numSets, setNumSets] = useState(2);
  const [certificationStatus, setCertificationStatus] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSetModal, setShowSetModal] = useState(false);
  const [selectedSetDetails, setSelectedSetDetails] = useState(null);

  const checkCertification = useCallback(async () => {
    try {
      const { data } = await API.get('/faculty-certification/my-certification');
      if (data.success) {
        setCertificationStatus(data.certification);
        if (!data.certification.is_certified) {
          showToast('University certification required to access this feature', 'error');
        }
      }
    } catch (err) {
      showToast('Failed to verify certification status', 'error');
      setLoading(false);
    }
  }, [showToast]);

  const fetchPoolsReady = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/university-pool-moderator/pools-ready');
      
      if (data.success) {
        setPoolsReady(data.pools || []);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to fetch pools ready for moderation', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    checkCertification();
  }, [checkCertification]);

  useEffect(() => {
    if (certificationStatus?.is_certified) {
      fetchPoolsReady();
    }
  }, [certificationStatus, fetchPoolsReady]);

  const fetchSelectedVariations = async (poolId) => {
    try {
      setLoading(true);
      const { data } = await API.get(`/university-pool-moderator/pools/${poolId}/selected-variations`);
      
      if (data.success) {
        setSelectedVariations(data);
        setSelectedPool(poolId);
        setActiveTab('generate');
        fetchGeneratedSets(poolId);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to fetch selected variations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneratedSets = async (poolId) => {
    try {
      const { data } = await API.get(`/university-pool-moderator/pools/${poolId}/sets`);
      
      if (data.success) {
        setGeneratedSets(data.sets || []);
      }
    } catch (err) {
      console.error('Failed to fetch generated sets:', err);
    }
  };

  const handleGenerateSets = async () => {
    if (numSets < 1 || numSets > 4) {
      showToast('Please enter 1-4 sets to generate', 'error');
      return;
    }

    try {
      setGenerating(true);
      const { data } = await API.post(`/university-pool-moderator/pools/${selectedPool}/generate-sets`, {
        num_sets: numSets
      });

      if (data.success) {
        showToast(`Successfully generated ${data.sets_generated} unique sets`, 'success');
        fetchGeneratedSets(selectedPool);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to generate sets', 'error');
    } finally {
      setGenerating(false);
    }
  };

  // const handleDeleteSet = async (setId) => {
  //   if (!window.confirm('Are you sure you want to delete this set?')) {
  //     return;
  //   }

  //   try {
  //     const { data } = await API.delete(`/university-pool-moderator/pools/${selectedPool}/sets/${setId}`);

  //     if (data.success) {
  //       showToast('Set deleted successfully', 'success');
  //       fetchGeneratedSets(selectedPool);
  //     }
  //   } catch (err) {
  //     showToast(err.response?.data?.message || 'Failed to delete set', 'error');
  //   }
  // };

  const handleConfirmSets = async () => {
    try {
      setSendingToCOE(true);
      const { data } = await API.post(`/university-pool-moderator/pools/${selectedPool}/confirm-sets`);

      if (data.success) {
        showToast('3 categorized sets confirmed and sent to COE', 'success');
        setShowConfirmModal(false);
        fetchPoolsReady();
        fetchGeneratedSets(selectedPool);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to confirm sets', 'error');
    } finally {
      setSendingToCOE(false);
    }
  };

  // const fetchCategorizationStatus = async (poolId) => {
  //   try {
  //     const { data } = await API.get(`/university-pool-moderator/pools/${poolId}/categorization-status`);
      
  //     if (data.success) {
  //       setSetsByCategory({
  //         general: data.sets.filter(s => s.category === 'general'),
  //         special: data.sets.filter(s => s.category === 'special'),
  //         reexam: data.sets.filter(s => s.category === 'reexam')
  //       });
  //     }
  //   } catch (err) {
  //     console.error('Failed to fetch categorization status:', err);
  //   }
  // };

  const viewSetDetails = async (setId) => {
    try {
      const { data } = await API.get(`/university-pool-moderator/pools/${selectedPool}/sets/${setId}`);
      
      if (data.success) {
        setSelectedSetDetails(data.set);
        setShowSetModal(true);
      }
    } catch (err) {
      showToast('Failed to fetch set details', 'error');
    }
  };

  const calculateMaxPossibleSets = () => {
    if (!selectedVariations || !selectedVariations.structure) return 0;
    
    let max = 1;
    for (const subQ of selectedVariations.structure) {
      max *= subQ.variations_count;
      if (max > 10000000) return 10000000;
    }
    return max;
  };

  // Show certification requirement if not certified
  if (!certificationStatus?.is_certified) {
    return (
      <div className="university-pool-moderator-container">
        <div className="page-header">
          <h1>University Question Pool - Moderator</h1>
          <p>University certification required to access this feature</p>
        </div>
        
        <div className="certification-required">
          <div className="certification-card">
            <div className="certification-icon">🎓</div>
            <h2>University Certification Required</h2>
            <p>
              To access the University Question Pool system, you need to be certified by your University Admin.
              This certification allows you to generate sets from university-wide question pools.
            </p>
            <div className="certification-benefits">
              <h3>Benefits of University Certification:</h3>
              <ul>
                <li>Generate sets from university-wide pools</li>
                <li>Create unique paper combinations</li>
                <li>Contribute to cross-college examinations</li>
                <li>Enhanced professional recognition</li>
              </ul>
            </div>
            <p className="certification-contact">
              Please contact your University Admin to request certification.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="university-pool-moderator-container">
      <div className="page-header">
        <h1>University Question Pool - Moderator</h1>
        <p>Generate unique question paper sets from SME-selected variations</p>
        {user.certification_level === 'university' && (
          <div className="certification-badge">
            <span className="badge university-certified">University Certified</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs-header">
          <button 
            className={`tab-button ${activeTab === 'pools' ? 'active' : ''}`}
            onClick={() => setActiveTab('pools')}
          >
            Pools Ready ({poolsReady.length})
          </button>
          {selectedPool && (
            <button 
              className={`tab-button ${activeTab === 'generate' ? 'active' : ''}`}
              onClick={() => setActiveTab('generate')}
            >
              Generate Sets
            </button>
          )}
        </div>

        <div className="tab-content">
          {activeTab === 'pools' && (
            <div className="pools-section">
              <div className="section-header">
                <h2>Pools Ready for Set Generation</h2>
                <p>Select a pool to generate unique question paper sets</p>
              </div>

              {poolsReady.length === 0 ? (
                <div className="empty-state">
                  <p>No pools ready for set generation</p>
                </div>
              ) : (
                <div className="pools-grid">
                  {poolsReady.map(pool => (
                    <div key={pool.pool_paper_id} className="pool-card">
                      <div className="pool-header">
                        <h3>{pool.subject_name} - {pool.paper_title || 'General'}</h3>
                        <span className={`status-badge ${pool.workflow_status}`}>
                          SME Completed
                        </span>
                      </div>
                      
                      <div className="pool-details">
                        <p><strong>Available Sets:</strong> {pool.available_sets}</p>
                        <p><strong>Selected Sets:</strong> {pool.selected_sets}</p>
                        <p><strong>Completed:</strong> {new Date(pool.moderator_completed_at).toLocaleDateString()}</p>
                        <p><strong>Total Marks:</strong> {pool.total_marks}</p>
                      </div>

                      <div className="pool-actions">
                        <button 
                          className="btn btn-primary"
                          onClick={() => fetchSelectedVariations(pool.pool_paper_id)}
                        >
                          Generate Sets
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'generate' && selectedVariations && (
            <div className="generate-section">
              <div className="section-header">
                <h2>{selectedVariations.pool.paper_title} - Set Generation</h2>
                <p>Generate unique combinations from SME-selected variations</p>
              </div>

              {/* Generation Controls */}
              <div className="generation-controls">
                <div className="control-group">
                  <label className="form-label">Number of Sets to Generate</label>
                  <input
                    type="number"
                    className="form-input"
                    value={numSets}
                    onChange={(e) => setNumSets(parseInt(e.target.value))}
                    min="1"
                    max="4"
                  />
                  <small className="form-help">Maximum 4 sets allowed</small>
                </div>
                
                <div className="generation-info">
                  <p><strong>Max Possible Unique Sets:</strong> {calculateMaxPossibleSets().toLocaleString()}</p>
                  <p><strong>Sub-questions:</strong> {selectedVariations.structure.length}</p>
                </div>
                
                <div className="generation-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={handleGenerateSets}
                    disabled={generating || generatedSets.length > 0}
                  >
                    {generating ? 'Generating...' : 'Generate Sets'}
                  </button>
                  
                  {generatedSets.length > 0 && (
                    <button 
                      className="btn btn-success"
                      onClick={() => setShowConfirmModal(true)}
                    >
                      Confirm & Send to COE ({generatedSets.length} sets)
                    </button>
                  )}
                </div>
              </div>

              {/* Variation Structure */}
              <div className="variation-structure">
                <h3>Selected Variations Structure</h3>
                <div className="structure-grid">
                  {selectedVariations.structure.map(subQuestion => (
                    <div key={subQuestion.pool_sub_question_id} className="structure-item">
                      <h4>{subQuestion.full_question_number}</h4>
                      <p><strong>Type:</strong> {subQuestion.question_type}</p>
                      <p><strong>Marks:</strong> {subQuestion.marks}</p>
                      <p><strong>Selected Variations:</strong> {subQuestion.variations_count}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Generated Sets - Moderator Categorized View */}
              {generatedSets.length > 0 && (
                <ModeratorCategorizedSetsView 
                  sets={generatedSets}
                  setsByCategory={setsByCategory}
                  poolTitle={selectedVariations.pool.paper_title}
                  onViewDetails={viewSetDetails}
                  onSendToCOE={handleConfirmSets}
                  loading={sendingToCOE}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirm Sets Modal - Updated for Categorization */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send Categorized Sets to COE</h3>
              <button 
                className="modal-close"
                onClick={() => setShowConfirmModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>You are about to send the following 3 categorized sets to COE:</p>
              
              <div className="sets-summary">
                <div className="summary-item general">
                  <span className="icon">📘</span>
                  <span className="label">General Exam</span>
                  <span className="value">{setsByCategory.general.length > 0 ? `Set ${setsByCategory.general[0].set_number}` : 'Not assigned'}</span>
                </div>
                <div className="summary-item special">
                  <span className="icon">📗</span>
                  <span className="label">Special Case</span>
                  <span className="value">{setsByCategory.special.length > 0 ? `Set ${setsByCategory.special[0].set_number}` : 'Not assigned'}</span>
                </div>
                <div className="summary-item reexam">
                  <span className="icon">📙</span>
                  <span className="label">Re-Examination</span>
                  <span className="value">{setsByCategory.reexam.length > 0 ? `Set ${setsByCategory.reexam[0].set_number}` : 'Not assigned'}</span>
                </div>
              </div>
              
              <p className="warning-text">
                ⚠️ After confirmation, you cannot make changes to these sets. Non-categorized sets will be deleted. COE will review and select final sets.
              </p>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-success"
                onClick={handleConfirmSets}
                disabled={sendingToCOE}
              >
                {sendingToCOE ? 'Sending...' : 'Confirm & Send to COE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set Details Modal */}
      {showSetModal && selectedSetDetails && (
        <div className="modal-overlay" onClick={() => setShowSetModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Set {selectedSetDetails.set_number} - Details</h3>
              <button 
                className="modal-close"
                onClick={() => setShowSetModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="set-info">
                <p><strong>Total Marks:</strong> {selectedSetDetails.total_marks}</p>
                <p><strong>Status:</strong> {selectedSetDetails.status}</p>
                <p><strong>Created:</strong> {new Date(selectedSetDetails.created_at).toLocaleDateString()}</p>
              </div>

              <div className="questions-list">
                <h4>Questions in this Set:</h4>
                {Object.entries(selectedSetDetails.questions).map(([subQuestionId, questionData]) => (
                  <div key={subQuestionId} className="question-item">
                    <div className="question-header">
                      <h5>{questionData.full_question_number}</h5>
                      <span className="variation-number">Variation #{questionData.variation.variation_number}</span>
                    </div>
                    
                    <div className="question-content">
                      <p><strong>Question:</strong> {questionData.variation.question_text}</p>
                      
                      {questionData.variation.options && (
                        <div className="question-options">
                          <strong>Options:</strong>
                          <ul>
                            {JSON.parse(questionData.variation.options).map((option, index) => (
                              <li key={index}>{option}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <p><strong>Answer:</strong> {questionData.variation.correct_answer}</p>
                      <p><strong>Marks:</strong> {questionData.variation.marks}</p>
                      <p><strong>Quality Score:</strong> {questionData.variation.quality_score}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowSetModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversityPoolModerator;